import { AppConfig } from "../../../../shared/app-configs/app-config";
import { TCAppConfig } from "../../../../shared/app-configs/tc-app-configs";
import apiService from "../../../../shared/service/api-service";

// ─── Session-scoped reference-data cache ─────────────────────────────────────
// Stores in-flight Promises by key so that:
//   • Concurrent callers awaiting the same key share ONE network request.
//   • Resolved responses are returned instantly on subsequent calls.
//   • Failed requests are evicted so the next call retries cleanly.
// Call clearServiceCache() on user logout to prevent stale data in new sessions.
const _cache = new Map<string, Promise<any>>();

function cached(key: string, fetcher: () => Promise<any>): Promise<any> {
    if (!_cache.has(key)) {
        _cache.set(
            key,
            fetcher().catch((err) => {
                _cache.delete(key); // allow retry on failure
                throw err;
            })
        );
    }
    return _cache.get(key)!;
}

/** Call this on logout / TC change to purge session-cached reference data. */
export function clearServiceCache(): void {
    _cache.clear();
}
// ─────────────────────────────────────────────────────────────────────────────

const normalizeReferenceResponse = (response: any) => {
    if (Array.isArray(response?.data?.value)) {
        return {
            ...response,
            data: response.data.value,
            referenceMeta: response.data,
        };
    }

    return response;
};

const getReferenceCodes = (queryParam: string) => {
    return cached(`ref:${queryParam}`, async () => {
        const response = await apiService.get<any>(TCAppConfig.DTRACKIT_BASE_ENDPOINT_V4, {
            query_param: queryParam
        });
        return normalizeReferenceResponse(response);
    });
};

export const dtrackItService = {
    async getReferenceCodes(queryParam: string) {
        return getReferenceCodes(queryParam);
    },
    async getTCStandards() {
        return getReferenceCodes(TCAppConfig.GET_TC_STANDARD_CODE_V4);
    },
    async getDelayReasons() {
        return getReferenceCodes(TCAppConfig.GET_DELAY_REASON_CODE_V4);
    },
    async getAmendmentReasons() {
        return getReferenceCodes(TCAppConfig.GET_AMENDMENT_REASON_CODE_V4);
    },
    async getInvalidationReasons() {
        return getReferenceCodes(TCAppConfig.GET_INVALIDATION_REASON_CODE_V4);
    },
    async getIncomingCertificateTypes() {
        return getReferenceCodes(TCAppConfig.GET_INCOMING_CERTIFICATE_TYPE_CODE_V4);
    },
    async getYesNoOptions() {
        return getReferenceCodes(TCAppConfig.GET_YES_NO_CODE_V4);
    },
    getOrganicFarmStandards() {
        return cached("organicFarmStandards", async () => {
            const queryParams = {
                query_param: TCAppConfig.GET_ORGANIC_FARM_CODE
            };
            const response = await apiService.get<any>(TCAppConfig.DTRACKIT_BASE_ENDPOINT_V4, queryParams);
            return normalizeReferenceResponse(response);
        });
    },
    // Additional v4 methods
    getTCStatus() {
        return cached("tcStatus", async () => {
            const queryParams = {
                query_param: TCAppConfig.GET_TC_STATUS
            };
            const response = await apiService.get<any>(TCAppConfig.DTRACKIT_BASE_ENDPOINT_V4, queryParams);
            return normalizeReferenceResponse(response);
        });
    },
    getStandradLabelGrade() {
        return cached("standardLabelGrade", async () => {
            const queryParams = {
                query_param: TCAppConfig.GET_STANDARD_LABEL_GRADE_V4
            };
            const response = await apiService.get<any>(TCAppConfig.DTRACKIT_BASE_ENDPOINT_V4, queryParams);
            return normalizeReferenceResponse(response);
        });
    },

    getProductCategory() {
        return cached("productCategory", async () => {
            const queryParams = {
                query_param: TCAppConfig.GET_PRODUCT_CODE_V4
            };
            const response = await apiService.get<any>(TCAppConfig.DTRACKIT_BASE_ENDPOINT_V4, queryParams);
            return normalizeReferenceResponse(response);
        });
    },

    getProductDetailsCode() {
        return cached("productDetailsCode", async () => {
            const queryParams = {
                query_param: TCAppConfig.GET_PRODUCT_DETAIL_CODE_V4
            };
            const response = await apiService.get<any>(TCAppConfig.DTRACKIT_BASE_ENDPOINT_V4, queryParams);
            return normalizeReferenceResponse(response);
        });
    },

    getCountry() {
        return cached("countries", async () => {
            const queryParams = {
                query_param: TCAppConfig.GET_COUNTRY_CODE_V4
            };
            const response = await apiService.get<any>(TCAppConfig.DTRACKIT_BASE_ENDPOINT_V4, queryParams);
            const normalized = normalizeReferenceResponse(response);
            if (Array.isArray(normalized?.data)) {
                normalized.data.sort((a: any, b: any) => {
                    const nameA = (a?.description_1 || a?.label || a?.code_1 || "").toString();
                    const nameB = (b?.description_1 || b?.label || b?.code_1 || "").toString();
                    return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
                });
            }
            return normalized;
        });
    },

    getStateBasedonCountry(country: any) {
        // Keyed per country so each country's states are cached independently
        let countryParam = country;
        try {
            if (country && typeof country === 'string') {
                const trimmed = country.trim();
                if (!trimmed.startsWith('[')) {
                    countryParam = `[${[trimmed].map((c: any) => `'${c}'`)}]`;
                } else {
                    countryParam = trimmed;
                }
            }
        } catch (e) {
            countryParam = country;
        }

        return cached(`states:${countryParam}`, async () => {
            const queryParams = {
                query_param: TCAppConfig.GET_STATE_CODE_V4,
                country: countryParam,
            };
            const response = await apiService.get<any>(TCAppConfig.DTRACKIT_BASE_ENDPOINT_V4, queryParams);
            return normalizeReferenceResponse(response);
        });
    },
    // v4: search state for Place of Issue using state_desc instead of country
    getStatesByDescription(stateDesc: string) {
        return cached(`statesByDesc:${stateDesc}`, async () => {
            const queryParams = {
                query_param: TCAppConfig.GET_STATE_CODE_V4,
                state_desc: stateDesc
            };
            const response = await apiService.get<any>(TCAppConfig.DTRACKIT_BASE_ENDPOINT_V4, queryParams);
            return normalizeReferenceResponse(response);
        });
    },
    getRawMaterialCodes(tcId?: string) {
        return cached(`rawMaterialCodes:${tcId || "global"}`, async () => {
            const queryParams: Record<string, any> = {
                query_param: TCAppConfig.GET_RAW_MATERIAL_CODE_V4
            };
            if (tcId) {
                queryParams.tcId = tcId;
            }
            const response = await apiService.get<any>(TCAppConfig.DTRACKIT_BASE_ENDPOINT_V4, queryParams);
            return normalizeReferenceResponse(response);
        });
    },
    getCBLicensingCodes() {
        return cached("cbLicensingCodes", async () => {
            const queryParams = {
                query_param: TCAppConfig.GET_CBLICENSING_CODE
            };
            const response = await apiService.get<any>(TCAppConfig.DTRACKIT_BASE_ENDPOINT_V4, queryParams);
            return normalizeReferenceResponse(response);
        });
    },
    getFacilityProcessCategoryCodes() {
        return cached("facilityProcessCategoryCodes", async () => {
            const queryParams = {
                query_param: TCAppConfig.GET_FACILITY_PROCESS_CATEGORY_CODE
            };
            const response = await apiService.get<any>(TCAppConfig.DTRACKIT_BASE_ENDPOINT_V4, queryParams);
            return normalizeReferenceResponse(response);
        });
    },
    // Facility TE-IDs listed on the TC's own seller SC(s), for scoping the
    // Product Facilities TE-ID search to what the seller is actually allowed
    // to declare a facility against.
    getFacilityTeIds(tcId: string) {
        return cached(`facilityTeIds:${tcId}`, async () => {
            try {
                const response = await apiService.get<any>(TCAppConfig.GET_FACILITY_TE_IDS_V4, { tcId });
                return Array.isArray(response?.data?.facilityTeIds) ? response.data.facilityTeIds : [];
            } catch (error) {
                // No seller SC resolvable yet (e.g. Buyer/Seller tab not saved,
                // or seller SC is "na") -- treat as "no facilities known".
                return [];
            }
        });
    },

    async getStandradLabelGradeOnBasisOfSelectedStandard(standard: string[]) {
        const expandedStandards = new Set<string>(standard);
        if (expandedStandards.has("MMS")) {
            ["RAS", "RCS", "GRS", "RDS", "RMS", "RWS"].forEach((std) => {
                expandedStandards.add(std);
            });
        }
        const finalStandards = Array.from(expandedStandards);
        const key = `standardLabelGrade:${[...finalStandards].sort().join(",")}`;
        return cached(key, async () => {
            const queryParams = {
                standard: [...finalStandards, "NA"],
            };
            const response = await apiService.get<any>(TCAppConfig.DTRACKIT_BASE_ENDPOINT_V4, queryParams);
            return normalizeReferenceResponse(response);
        });
    },

    getMetricsValues() {
        return cached("metricsValues", async () => {
            const queryParams = {
                query_param: TCAppConfig.GET_METRIC_CODES
            };
            const response = await apiService.get<any>(TCAppConfig.DTRACKIT_BASE_ENDPOINT_V4, queryParams);
            const normalized = normalizeReferenceResponse(response);
            if (Array.isArray(normalized?.data)) {
                normalized.data = normalized.data.filter((item: any) => {
                    const code = String(item?.code_1 || item?.code || item?.value || "").toLowerCase().trim();
                    const desc = String(item?.description_1 || item?.description || item?.label || "").toLowerCase().trim();
                    return code !== "lots" && code !== "lot" && desc !== "lots" && desc !== "lot";
                });
            }
            return normalized;
        });
    },
    // New methods for Seller SC based filtering
    getProductCategoryByTcId(tcId: string) {
        return cached(`productCategory:${tcId}`, async () => {
            const queryParams = {
                query_param: TCAppConfig.GET_PRODUCT_CODE_V4,
                tcId,
            };
            const response = await apiService.get<any>(TCAppConfig.DTRACKIT_BASE_ENDPOINT_V4, queryParams);
            return normalizeReferenceResponse(response);
        });
    },
    getProductDetailsCodeByTcId(tcId: string) {
        return cached(`productDetails:${tcId}`, async () => {
            const queryParams = {
                query_param: TCAppConfig.GET_PRODUCT_DETAIL_CODE_V4,
                tcId,
            };
            const response = await apiService.get<any>(TCAppConfig.DTRACKIT_BASE_ENDPOINT_V4, queryParams);
            return normalizeReferenceResponse(response);
        });
    },
    getSCProductClaimsCategory(scId: string) {
        return cached(`scProductClaims:${scId}`, async () => {
            const queryParams = {
                scId,
            };
            const response = await apiService.get<any>(TCAppConfig.GET_SC_PRODUCT_CLAIMS_CATEGORY_V4, queryParams);
            return normalizeReferenceResponse(response);
        });
    },
    getSellerSCProductClaimsCategory(tcId: string) {
        return cached(`sellerSCProductClaims:${tcId}`, async () => {
            const response = await apiService.get<any>(TCAppConfig.GET_SC_PRODUCT_CLAIMS_CATEGORY_V4, { tcId });
            return normalizeReferenceResponse(response);
        });
    },
};

